/**
 * @description Component-OrderHistory
 */
import './index.less';

import { Side } from '@synfutures/sdks-perp';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Empty from '@/components/Empty';
import { EmptyDataWrap } from '@/components/EmptyDataWrap';
import { ExternalLink } from '@/components/Link';
import TokenPairSimple from '@/components/TokenPair/TokenPairSimple';
import UnderlineToolTip from '@/components/ToolTip/UnderlineToolTip';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useInfiniteOrdersHistory } from '@/features/graph/hooks';
import { OrderHistory as OrderHistoryType } from '@/features/graph/types';
import { useCurrentPairFromUrl } from '@/features/pair/hook';
import { useChainId, useEtherscanLink, useUserAddr } from '@/hooks/web3/useChain';
import TradeSideSize from '@/pages/components/TradeSideSize';
import { formatDate } from '@/utils/timeUtils';
import { mappingToken } from '@/utils/token';
import MobileHistoryCard from '../MobileHistoryCard';
import MobileHistorySkeleton from '../MobileHistoryCard/Skeleton';

interface IPropTypes {
  isTradePage?: boolean;
}

const OrderHistory: FC<IPropTypes> = ({ isTradePage = false }) => {
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const { t } = useTranslation();
  const currentPair = useCurrentPairFromUrl(chainId);
  const pair = isTradePage ? currentPair : 'all';

  const {
    histories,
    queryState: { isLoading, isFetching },
  } = useInfiniteOrdersHistory({ chainId, userAddr, pair });

  const loading = isLoading || isFetching;

  const getEtherscanLink = useEtherscanLink();

  const renderCard = (record: OrderHistoryType) => {
    const side = record.side === 'long' ? Side.LONG : Side.SHORT;

    const fee = WrappedBigNumber.from(record.feeRebate || 0);

    // Map order type to status: 1=FILLED, 2=CANCELLED, 3=CANCELLED
    const orderType = record.typeString;
    let statusText = '';
    let txHash = '';
    let timestamp = 0;

    if (orderType === 'filled') {
      // Check if partially filled
      const takenSize = WrappedBigNumber.from(record.takenSize);
      const totalSize = WrappedBigNumber.from(record.size);
      if (!takenSize.eq(totalSize)) {
        statusText = t(`common.table.status.PARTIAL_FILLED`);
      } else {
        statusText = t(`common.table.status.FILLED`);
      }
      txHash = record.fillTxHash;
      timestamp = record.fillTimestamp;
    } else if (orderType === 'cancelled') {
      statusText = t(`common.table.status.CANCELLED`);
      txHash = record.cancelTxHash;
      timestamp = record.cancelTimestamp;
    }
    return (
      <MobileHistoryCard
        key={record.id}
        header={
          chainId ? (
            <>
              <div className="value">
                <EmptyDataWrap isLoading={!record?.instrumentAddress}>
                  <TokenPairSimple
                    tradeSide={record.side ? (record.side === 'long' ? Side.LONG : Side.SHORT) : Side.FLAT}
                    // isInverse={record.isInverse}
                    baseToken={mappingToken({
                      token: record.baseToken,
                      chainId: chainId,
                    })}
                    quoteToken={mappingToken({
                      token: record.quoteToken,
                      chainId: chainId,
                    })}
                  />
                </EmptyDataWrap>
              </div>
              <div className="value">
                <ExternalLink
                  href={getEtherscanLink(record.placeTxHash, 'transaction')}
                  className="history-time-with-icon">
                  {formatDate(record.placeTimestamp * 1000, 'MM-DD HH:mm')}
                </ExternalLink>
              </div>
            </>
          ) : undefined
        }>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.size')}</div>
          <div className="value">
            <div className="syn-side-size-table-item">
              <TradeSideSize
                side={side}
                size={WrappedBigNumber.from(record.size).formatNumberWithTooltip({
                  isShowTBMK: true,
                  suffix: record?.baseToken?.symbol,
                })}
              />
            </div>
          </div>
        </div>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.taken')}</div>
          <div className="value">
            {WrappedBigNumber.from(record.takenSize).abs().formatNumberWithTooltip({ isShowTBMK: true })}
          </div>
        </div>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.orderPrice')}</div>
          <div className="value">{WrappedBigNumber.from(record.orderPrice).formatPriceNumberWithTooltip()}</div>
        </div>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.tradeValue')}</div>
          <div className="value">
            {WrappedBigNumber.from(record.balance || 0)
              .abs()
              .formatNumberWithTooltip({
                suffix: record?.quoteToken?.symbol,
              })}
          </div>
        </div>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.feeEarned')}</div>
          <div className="value">
            <EmptyDataWrap isLoading={!record.feeRebate}>
              <span className={fee.gt(0) ? 'highlight' : ''}>
                {fee.formatNumberWithTooltip({
                  suffix: record?.quoteToken?.symbol,
                })}
              </span>
            </EmptyDataWrap>
          </div>
        </div>

        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.status')}</div>
          <div className="value">
            <UnderlineToolTip
              overlayStyle={{ maxWidth: 300 }}
              title={
                <ExternalLink className="tooltip" width={300} href={getEtherscanLink(txHash || '', 'transaction')}>
                  {t(`common.table.lastUpdatedTime`)}: {formatDate(timestamp * 1000)}
                </ExternalLink>
              }>
              <span>{statusText}</span>
            </UnderlineToolTip>
          </div>
        </div>
      </MobileHistoryCard>
    );
  };

  return (
    <>
      <div className="syn-mobile-history-order">
        {loading ? <MobileHistorySkeleton /> : histories.slice(0, 10).map(renderCard)}
      </div>
      {!loading && !!histories.length && <div className="syn-mobile-history-info">{t('mobile.history.tableInfo')}</div>}
      {!loading && !histories?.length && <Empty type="vertical" />}
    </>
  );
};

export default OrderHistory;
